import type { ApplicationConfig } from '@angular/core';
import { importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withInterceptors,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import {
  LucideAngularModule,
  Monitor,
  CircleDollarSign,
  Receipt,
  ChartBar,
  ArrowUpRight,
  AudioLines,
  LogOut,
  User,
  ChevronDown,
  Users,
  ArrowUpDown,
  SquarePen,
  UserPlus,
  ArrowLeft,
  X,
  Plus,
  CircleX,
  RefreshCw,
} from 'lucide-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalInterceptor,
  MsalGuard,
  MsalService,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  type MsalInterceptorConfiguration,
  type MsalGuardConfiguration,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { subscriptionKeyInterceptor } from './interceptors/subscription-key.interceptor';
import { teamsAuthInterceptor } from './interceptors/teams-auth.interceptor';
import { MockInterceptor } from './interceptors/mock.interceptor';

function msalLoggerCallback(logLevel: LogLevel, message: string): void {
  if (logLevel === LogLevel.Error) {
    console.error('[MSAL]', message);
  } else if (!environment.production) {
    console.debug('[MSAL]', message);
  }
}

function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: environment.msal.authority,
      redirectUri: environment.msal.redirectUri,
      postLogoutRedirectUri: environment.msal.postLogoutRedirectUri,
    },
    cache: {
      // LocalStorage (not SessionStorage) so the MSAL token cache is shared across
      // sibling apps on the same origin (WorkAssist / CTS / FPNA all live under
      // workassist.<env>.abm.com) and survives new-tab launches. This lets a child
      // app reuse the token WorkAssist already acquired instead of re-authenticating.
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        loggerCallback: msalLoggerCallback,
        logLevel: environment.production ? LogLevel.Error : LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.msal.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string> | null>();
  const apiOrigin = new URL(environment.apiConfig.serverBaseUrl).origin;
  protectedResourceMap.set(`${apiOrigin}/*`, environment.apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Builds the application providers.
 *
 * @param isInTeams whether the app is hosted inside the Microsoft Teams client.
 *   In Teams there is no MSAL redirect session, so the MSAL interceptor is
 *   replaced by teamsAuthInterceptor (Teams SSO -> OBO). Detected in main.ts
 *   before bootstrap. Defaults to false for the standalone browser flow.
 */
export function createAppConfig(isInTeams = false): ApplicationConfig {
  const useMsal = !environment.mockMode && !isInTeams;

  return {
  providers: [
    provideRouter(routes),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors(
        isInTeams
          ? [subscriptionKeyInterceptor, teamsAuthInterceptor]
          : [subscriptionKeyInterceptor]
      )
    ),
    importProvidersFrom(BrowserModule),
    importProvidersFrom(
      LucideAngularModule.pick({
        Monitor,
        CircleDollarSign,
        Receipt,
        ChartBar,
        ArrowUpRight,
        AudioLines,
        LogOut,
        User,
        ChevronDown,
        Users,
        ArrowUpDown,
        SquarePen,
        UserPlus,
        ArrowLeft,
        X,
        Plus,
        CircleX,
        RefreshCw,
      })
    ),
    { provide: HTTP_INTERCEPTORS, useClass: MockInterceptor, multi: true },
    ...(useMsal ? [
      { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
      { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    ] : []),
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  };
}

/** Default browser configuration (non-Teams). Retained for compatibility. */
export const appConfig: ApplicationConfig = createAppConfig(false);
